/**
 * labelEventGraphApi.js
 *
 * Contrat API pour LabelEventGraph — adapté au pattern réel de l'application.
 *
 * Pattern de l'app : UN seul endpoint POST /api avec un champ `request_type`.
 * Pas de REST classique — toutes les opérations passent par ce tunnel unique.
 *
 * ─── Nouveaux request_type à implémenter côté serveur ────────────────────────
 *
 *  recuperation_des_donnees_d_un_graphe          (existant — réponse enrichie)
 *  creation_label_event_graph                    (nouveau)
 *  modification_label_event_graph                (nouveau)
 *  suppression_label_event_graph                 (nouveau)
 *  creation_section_event_graph                  (nouveau)
 *  modification_section_event_graph              (nouveau)
 *  suppression_section_event_graph               (nouveau)
 *
 * ─── Enrichissement de la réponse existante ──────────────────────────────────
 *
 * La réponse de recuperation_des_donnees_d_un_graphe doit être enrichie
 * avec deux champs supplémentaires :
 *
 *   donneesGraphe = {
 *     config:   { graph_id, graph_type_id, title, xaxis, yaxis, ... },
 *     data:     [ { x, y, name, sensor_name, ... } ],         // courbe signal
 *     labels:   [ Label, ... ],    ← NOUVEAU
 *     sections: [ Section, ... ],  ← NOUVEAU
 *   }
 *
 * ─── Shapes ──────────────────────────────────────────────────────────────────
 *
 * Label : {
 *   id        : number,
 *   graph_id  : number,
 *   t         : number,            // timestamp unix du point annoté
 *   label_type: 'IA' | 'JALON',
 *   icon_type : 'circle' | 'diamond',
 *   label     : string,
 *   note      : string,
 * }
 *
 * Section : {
 *   id        : number,
 *   graph_id  : number,
 *   t_start   : number,            // timestamp unix début
 *   t_end     : number,            // timestamp unix fin
 *   label     : string,
 *   color     : string,            // hex e.g. '#e07b00'
 * }
 */

// ─── Helpers ──────────────────────────────────────────────────────────────────

const ok = (data) => ({ ok: true, data });
const err = (message) => ({ ok: false, error: message });
const delay = (ms = 120) => new Promise((r) => setTimeout(r, ms));

/**
 * Enveloppe le pattern réel de l'app.
 * En production : remplacer le corps de cette fonction par un appel axios/fetch
 * identique à celui de src/services/api.ts (_postSafe).
 */
async function _post(request_type, payload) {
  // MOCK — simuler le délai réseau
  await delay();
  return (
    MOCK_HANDLERS[request_type]?.(payload) ??
    err(`request_type inconnu : ${request_type}`)
  );
}

// ─── API publique ─────────────────────────────────────────────────────────────

/**
 * Récupère toutes les données du graphe : signal + labels + sections.
 * Utilise le request_type existant, enrichi côté serveur.
 *
 * Réponse : {
 *   config, data,
 *   labels:   Label[],
 *   sections: Section[],
 * }
 */
export function fetchGraphData(graph_id, options = {}) {
  return _post('recuperation_des_donnees_d_un_graphe', {
    graph_id,
    time_period: options.time_period ?? null,
    measures_num: options.measures_num ?? null,
    select_time: options.select_time ?? [],
    time_interval: options.time_interval ?? [null, null],
    id_sensor: options.id_sensor ?? [],
  });
}

/**
 * Crée un label sur le graphe.
 * Payload : { graph_id, t, label_type, icon_type, label, note }
 * Réponse : Label créé (avec id généré par le serveur)
 */
export function createLabel(graph_id, payload) {
  return _post('creation_label_event_graph', { graph_id, ...payload });
}

/**
 * Met à jour un label existant.
 * Payload : { label_id, t?, label_type?, icon_type?, label?, note? }
 * Réponse : Label mis à jour
 */
export function updateLabel(graph_id, label_id, payload) {
  return _post('modification_label_event_graph', {
    graph_id,
    label_id,
    ...payload,
  });
}

/**
 * Supprime un label.
 * Réponse : { deleted: true }
 */
export function deleteLabel(graph_id, label_id) {
  return _post('suppression_label_event_graph', { graph_id, label_id });
}

/**
 * Crée une section (phase).
 * Payload : { graph_id, t_start, t_end, label, color }
 * Réponse : Section créée (avec id généré par le serveur)
 */
export function createSection(graph_id, payload) {
  return _post('creation_section_event_graph', { graph_id, ...payload });
}

/**
 * Met à jour une section existante.
 * Payload : { section_id, t_start?, t_end?, label?, color? }
 * Réponse : Section mise à jour
 */
export function updateSection(graph_id, section_id, payload) {
  return _post('modification_section_event_graph', {
    graph_id,
    section_id,
    ...payload,
  });
}

/**
 * Supprime une section.
 * Réponse : { deleted: true }
 */
export function deleteSection(graph_id, section_id) {
  return _post('suppression_section_event_graph', { graph_id, section_id });
}

// ─── Handlers MOCK ────────────────────────────────────────────────────────────
// Simulent les réponses du serveur. Supprimer ce bloc quand le backend est prêt.

const T0 = 1772323200;
const D = 86400;

const MOCK_SIGNAL_DATA = {
  name: 'Niveau sonore — Capteur A-03',
  sensor_name: 'A-03',
  id_sensor: 109755,
  position_in_sensor: 1,
  prefix: 'dB',
  engineering_unit: 'dB',
  type: 'scatter',
  mode: 'lines',
  line: { color: '#7b2fa8', width: 2, shape: 'spline' },
  x: Array.from({ length: 60 }, (_, i) => T0 + i * 12 * 3600),
  y: [
    88.0, 105.9, 108.2, 100.9, 93.1, 92.3, 102.3, 112.8, 105.5, 80.1, 60.3,
    63.1, 76.2, 79.1, 70.0, 61.6, 61.8, 72.0, 90.1, 105.3, 104.9, 93.4, 90.8,
    103.5, 113.7, 106.1, 88.6, 76.5, 72.8, 72.5, 73.4, 72.1, 64.6, 58.4, 66.3,
    85.2, 96.2, 92.5, 89.1, 97.2, 400.0, 110.9, 105.1, 95.6, 84.9, 78.2, 80.1,
    82.6, 72.6, 56.1, 53.2, 68.7, 84.6, 87.2, 83.1, 83.7, 91.2, 103.3, 115.1,
    115.8,
  ],
};

let MOCK_SIGNAL_DATA_BIS = {
  name: 'Niveau sonore — Capteur A-04',
  sensor_name: 'A-04',
  id_sensor: 109756,
  position_in_sensor: 3,
  prefix: 'dB',
  engineering_unit: 'dB',
  type: 'scatter',
  mode: 'lines',
  line: { color: '#0077b6', width: 2, shape: 'spline' },
  x: Array.from({ length: 60 }, (_, i) => T0 + i * 12 * 3600),
  y: Array.from({ length: 60 }, () => Math.random() * (400 - 50) + 50),
};

let MOCK_LABELS = [
  {
    id: 1,
    graph_id: 1,
    t: T0 + 2 * D,
    label_type: 'IA',
    icon_type: 'circle',
    label: 'Excavatrice',
    note: 'Excavation rue Gambetta',
  },
  {
    id: 2,
    graph_id: 1,
    t: T0 + 8 * D,
    label_type: 'IA',
    icon_type: 'circle',
    label: 'Compresseur A',
    note: 'Compresseur chantier A',
  },
  {
    id: 3,
    graph_id: 1,
    t: T0 + 15 * D,
    label_type: 'IA',
    icon_type: 'circle',
    label: 'Trafic matinal',
    note: 'Pic trafic matin',
  },
  {
    id: 4,
    graph_id: 1,
    t: T0 + 20 * D,
    label_type: 'JALON',
    icon_type: 'diamond',
    label: 'Pic anormal',
    note: 'Valeur 400 dB',
  },
  {
    id: 5,
    graph_id: 1,
    t: T0 + 25 * D,
    label_type: 'JALON',
    icon_type: 'diamond',
    label: 'Marteau',
    note: 'Marteau-piqueur chantier B',
  },
];

let MOCK_SECTIONS = [
  {
    id: 1,
    graph_id: 1,
    t_start: T0,
    t_end: T0 + 10 * D,
    label: 'Phase 1 — Terrassement',
    color: '#e07b00',
  },
  {
    id: 2,
    graph_id: 1,
    t_start: T0 + 10 * D,
    t_end: T0 + 15 * D,
    label: 'Phase 2 — Fondations',
    color: '#0077b6',
  },
  {
    id: 3,
    graph_id: 1,
    t_start: T0 + 15 * D,
    t_end: T0 + 25 * D,
    label: 'Phase 3 — Finitions',
    color: '#26b36a',
  },
];

let _nextLabelId = 6;
let _nextSectionId = 4;

const MOCK_HANDLERS = {
  recuperation_des_donnees_d_un_graphe: ({ graph_id }) =>
    ok({
      config: {
        graph_id,
        graph_type_id: 34, // label_event_graph_geo
        title: 'Niveau sonore — Capteur A-03',
        subtitle: 'Mars 2026',
        xaxis: { title: 'Temps', type: 'date', range: [null, null] },
        yaxis: {
          title: 'Niveau sonore (dB)',
          type: 'linear',
          range: [null, null],
        },
      },
      id_sensor: [110634, 110636, 110637, 110638],
      graphCurves: [
        {
          curve_id: 256125,
          0: 256125,
          graph_id: 5491,
          1: 5491,
          name: '',
          2: '',
          show_alarms: 1,
          3: 1,
          formula: '#109755,1#',
          4: '#109755,1#',
          parameters: '',
          5: '',
          yaxis: 'y1',
          6: 'y1',
          type: 'bar',
          7: 'bar',
          mode: 'lines',
          8: 'lines',
          line_dash: 'solid',
          9: 'solid',
          line_shape: 'linear',
          10: 'linear',
          cumul_frequency: null,
          11: null,
          color: '#efb51a',
          12: '#efb51a',
        },
        {
          curve_id: 256126,
          0: 256126,
          graph_id: 5491,
          1: 5491,
          name: '',
          2: '',
          show_alarms: 1,
          3: 1,
          formula: '#109756,3#',
          4: '#109756,3#',
          parameters: '',
          5: '',
          yaxis: 'y2',
          6: 'y2',
          type: 'scatter',
          7: 'scatter',
          mode: 'lines',
          8: 'lines',
          line_dash: 'solid',
          9: 'solid',
          line_shape: 'spline',
          10: 'spline',
          cumul_frequency: null,
          11: null,
          color: '#a6163f',
          12: '#a6163f',
        },
      ],
      data: [MOCK_SIGNAL_DATA, MOCK_SIGNAL_DATA_BIS],
      labels: MOCK_LABELS.filter((l) => l.graph_id === graph_id),
      sections: MOCK_SECTIONS.filter((s) => s.graph_id === graph_id),
    }),

  creation_label_event_graph: ({ graph_id, ...payload }) => {
    const newLabel = { id: _nextLabelId++, graph_id, ...payload };
    MOCK_LABELS.push(newLabel);
    return ok(newLabel);
  },

  modification_label_event_graph: ({ graph_id, label_id, ...payload }) => {
    const idx = MOCK_LABELS.findIndex(
      (l) => l.id === label_id && l.graph_id === graph_id
    );
    if (idx === -1) return err('Label not found');
    MOCK_LABELS[idx] = { ...MOCK_LABELS[idx], ...payload };
    return ok(MOCK_LABELS[idx]);
  },

  suppression_label_event_graph: ({ graph_id, label_id }) => {
    const before = MOCK_LABELS.length;
    MOCK_LABELS = MOCK_LABELS.filter(
      (l) => !(l.id === label_id && l.graph_id === graph_id)
    );
    return MOCK_LABELS.length < before
      ? ok({ deleted: true })
      : err('Label not found');
  },

  creation_section_event_graph: ({ graph_id, ...payload }) => {
    const newSection = { id: _nextSectionId++, graph_id, ...payload };
    MOCK_SECTIONS.push(newSection);
    return ok(newSection);
  },

  modification_section_event_graph: ({ graph_id, section_id, ...payload }) => {
    const idx = MOCK_SECTIONS.findIndex(
      (s) => s.id === section_id && s.graph_id === graph_id
    );
    if (idx === -1) return err('Section not found');
    MOCK_SECTIONS[idx] = { ...MOCK_SECTIONS[idx], ...payload };
    return ok(MOCK_SECTIONS[idx]);
  },

  suppression_section_event_graph: ({ graph_id, section_id }) => {
    const before = MOCK_SECTIONS.length;
    MOCK_SECTIONS = MOCK_SECTIONS.filter(
      (s) => !(s.id === section_id && s.graph_id === graph_id)
    );
    return MOCK_SECTIONS.length < before
      ? ok({ deleted: true })
      : err('Section not found');
  },
};
